import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { useAdminQuickAccess, type AdminItem } from "@/lib/adminQuickAccess";

interface AdminCardProps {
  item: AdminItem;
  isFavourite: boolean;
  onToggleFavourite: (href: string) => void;
}

function AdminCard({ item, isFavourite, onToggleFavourite }: AdminCardProps) {
  const Icon = item.icon;
  return (
    <div className="relative group">
      <Link href={item.href}>
        <Card className="h-full cursor-pointer hover-elevate">
          <CardContent className="pt-6 pb-5 pr-10 space-y-3">
            <Icon className="h-6 w-6 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-sm">{item.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
            </div>
          </CardContent>
        </Card>
      </Link>
      <button
        className={`absolute top-3 right-3 p-1 rounded-md transition-opacity ${isFavourite ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFavourite(item.href);
        }}
        title={isFavourite ? "Remove from favourites" : "Add to favourites"}
      >
        <Star
          className={`h-4 w-4 transition-colors ${
            isFavourite
              ? "fill-yellow-400 text-yellow-400"
              : "text-muted-foreground/40 hover:text-muted-foreground"
          }`}
        />
      </button>
    </div>
  );
}

export default function AdminHome() {
  const { favourites, toggleFavourite, frequentItems, favouriteItems } = useAdminQuickAccess();

  const hasQuickAccess = favouriteItems.length > 0 || frequentItems.length > 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Admin tools for managing EduFocal.</p>
      </div>

      {hasQuickAccess ? (
        <div className="space-y-4">
          {favouriteItems.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Pinned</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {favouriteItems.map((item) => (
                  <AdminCard
                    key={item.href}
                    item={item}
                    isFavourite={true}
                    onToggleFavourite={toggleFavourite}
                  />
                ))}
              </div>
            </div>
          )}
          {frequentItems.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Recently Visited</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {frequentItems.map((item) => (
                  <AdminCard
                    key={item.href}
                    item={item}
                    isFavourite={false}
                    onToggleFavourite={toggleFavourite}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="py-12 text-center space-y-2">
          <Star className="h-8 w-8 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground">No pages visited yet.</p>
          <p className="text-xs text-muted-foreground/70">
            Pages you visit will appear here. Hover a sidebar item and click the star to pin your favourites.
          </p>
        </div>
      )}

    </div>
  );
}
